import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { tombac } from "../../shared/tombac";
export function useRipple(_ref) {
  let {
    trigger
  } = _ref;
  const [rippleProps, setRippleProps] = useState();
  const hasHoverRef = useRef(false);
  const triggerProps = useMemo(() => ({
    onKeyDown(event) {
      if (event.key === "Enter" || event.key === " ") {
        setRippleProps({
          coords: {
            x: 0,
            y: 0
          },
          pressedKey: event.key,
          phase: "hold"
        });
      }
    },
    onKeyUp(event) {
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      setRippleProps(rippleProps => {
        if (!(rippleProps !== null && rippleProps !== void 0 && rippleProps.pressedKey)) return;
        return {
          coords: {
            x: rect.width / 2,
            y: rect.height / 2
          },
          phase: "released"
        };
      });
    },
    onPointerDown(event) {
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      setRippleProps(() => {
        setTimeout(() => setRippleProps(rippleProps => {
          if ((rippleProps === null || rippleProps === void 0 ? void 0 : rippleProps.phase) !== "idle") return rippleProps;
          return {
            ...rippleProps,
            phase: "hold"
          };
        }));
        return {
          boundingRect: rect,
          coords: {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
          },
          phase: "idle" // set "idle" first to trigger "hold" animation in the next tick
        };
      });
    },
    onPointerEnter() {
      hasHoverRef.current = true;
    },
    onPointerLeave() {
      hasHoverRef.current = false;
    }
  }), []);
  const handlePointerUp = useCallback(event => {
    setRippleProps(rippleProps => {
      const rect = rippleProps === null || rippleProps === void 0 ? void 0 : rippleProps.boundingRect;
      if (!rect) return rippleProps;
      return {
        ...rippleProps,
        boundingRect: undefined,
        coords: {
          x: hasHoverRef.current ? event.clientX - rect.left : 0,
          y: hasHoverRef.current ? event.clientY - rect.top : 0
        },
        phase: hasHoverRef.current ? "released" : "canceled"
      };
    });
  }, []);
  useEffect(() => {
    if (!trigger) return;
    document.addEventListener("pointerup", handlePointerUp);
    return () => {
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handlePointerUp, trigger]);
  return {
    rippleProps,
    triggerProps
  };
}
let instances = 0;
export const Ripple = _ref2 => {
  let {
    coords,
    ...props
  } = _ref2;
  const theme = useTheme();
  const maskId = useMemo(() => `ripple-mask-${++instances}`, []);
  const circle = /*#__PURE__*/React.createElement(Circle, _extends({}, props, {
    cx: "50",
    cy: "50",
    r: props.phase === "hold" ? "0" : "50"
  }));
  return /*#__PURE__*/React.createElement(ContainerSvg, _extends({}, props, {
    "aria-hidden": true,
    viewBox: "0 0 100 100",
    style: {
      left: coords.x,
      top: coords.y
    }
  }), /*#__PURE__*/React.createElement(BackgroundRect, {
    x: "0",
    y: "0",
    width: "100",
    height: "100",
    mask: theme.components.ripple.circleFill === "transparent" ? `url(#${maskId})` : undefined
  }), theme.components.ripple.circleFill === "transparent" ? /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("mask", {
    id: maskId,
    x: "0",
    y: "0",
    width: "100",
    height: "100"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "0",
    y: "0",
    width: "100",
    height: "100",
    fill: "#fff"
  }), circle)) : circle);
};
const BackgroundRect = styled.rect.withConfig({
  displayName: "Ripple__BackgroundRect"
})(["fill:", ";"], _ref3 => {
  let {
    theme
  } = _ref3;
  return tombac.parse(theme.components.ripple.fill);
});
const Circle = styled.circle.withConfig({
  displayName: "Ripple__Circle"
})(["fill:", ";transform-origin:50%;transition-property:r;transition-timing-function:ease-out;", ""], _ref4 => {
  let {
    theme: {
      components: {
        ripple
      }
    }
  } = _ref4;
  return ripple.circleFill === "transparent" ? "#000" : tombac.parse(ripple.circleFill);
}, tombac.variant("phase", {
  $default: css(["transition-duration:0s;"]),
  released: css(["transition-duration:0.3s;"])
}));
const ContainerSvg = styled.svg.withConfig({
  displayName: "Ripple__ContainerSvg"
})(["opacity:0;position:absolute;transform:translate(-50%,-50%);transition:opacity 0.1s ease-out;width:200%;", ""], tombac.variant("phase", {
  canceled: css(["opacity:0;"]),
  idle: css(["opacity:0;transition-duration:0s;"]),
  hold: css(["opacity:1;"]),
  released: css(["opacity:0;transition-delay:0.3s;transition-duration:0.5s;"])
}));
//# sourceMappingURL=Ripple.js.map